/**
 * AI Development Integration Service
 * Optional integration with ai-dev-server for enhanced development experience
 * Falls back gracefully if the service is not available
 */
import { Utils } from '../utils/Utils.js';

export class AIDevIntegration {
    constructor() {
        this.isAvailable = false;
        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 3;
        this.websocketPort = 3001; // Default port for ai-dev-server
        this.retryDelay = 2000;
    }

    /**
     * Try to connect to the AI dev server
     */
    async connect() {
        try {
            // Only try to connect in development
            if (!this.isDevelopment()) {
                Utils.debug('AI dev integration disabled in production');
                return false;
            }

            // Double-check we're not in production
            if (window.location.hostname.includes('pages.dev') || 
                window.location.hostname.includes('workers.dev') ||
                window.location.hostname.includes('cloudflare')) {
                Utils.debug('AI dev integration disabled on Cloudflare');
                return false;
            }

            Utils.info('Attempting to connect to AI dev server...');
            
            const wsUrl = `ws://localhost:${this.websocketPort}`;
            this.ws = new WebSocket(wsUrl);

            return new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    Utils.debug('AI dev server connection timeout');
                    this.cleanup();
                    resolve(false);
                }, 3000);

                this.ws.onopen = () => {
                    clearTimeout(timeout);
                    this.isAvailable = true;
                    this.reconnectAttempts = 0;
                    Utils.info('Connected to AI dev server');
                    this.setupEventHandlers();
                    resolve(true);
                };

                this.ws.onerror = () => {
                    clearTimeout(timeout);
                    Utils.debug('AI dev server not available');
                    this.cleanup();
                    resolve(false);
                };

                this.ws.onclose = () => {
                    clearTimeout(timeout);
                    if (this.isAvailable) {
                        Utils.warn('AI dev server disconnected');
                        this.attemptReconnect();
                    }
                    resolve(false);
                };
            });

        } catch (error) {
            Utils.debug('AI dev server connection failed:', error);
            return false;
        }
    }

    /**
     * Setup event handlers for AI dev server communication
     */
    setupEventHandlers() {
        this.ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this.handleMessage(message);
            } catch (error) {
                Utils.error('Failed to parse AI dev server message:', error);
            }
        };
    }

    /**
     * Handle messages from AI dev server
     */
    handleMessage(message) {
        switch (message.type) {
            case 'console_log':
                Utils.debug('AI Dev Console:', message.data);
                break;
            case 'error':
                Utils.error('AI Dev Error:', message.data);
                break;
            case 'screenshot':
                Utils.debug('AI Dev Screenshot taken:', message.data);
                break;
            case 'ai_suggestion':
                this.handleAISuggestion(message.data);
                break;
        }
    }

    /**
     * Handle AI suggestions
     */
    handleAISuggestion(suggestion) {
        Utils.info('AI Suggestion:', suggestion.message);
        
        // Dispatch custom event for UI to handle
        window.dispatchEvent(new CustomEvent('aiSuggestion', {
            detail: suggestion
        }));
    }

    /**
     * Send message to AI dev server
     */
    send(message) {
        if (this.isAvailable && this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify(message));
            } catch (error) {
                Utils.error('Failed to send message to AI dev server:', error);
            }
        }
    }

    /**
     * Report app status to AI dev server
     */
    reportStatus(status) {
        this.send({
            type: 'app_status',
            data: {
                timestamp: Date.now(),
                status: status,
                url: window.location.href,
                userAgent: navigator.userAgent
            }
        });
    }

    /**
     * Report WebRTC connection status
     */
    reportWebRTCStatus(status) {
        this.send({
            type: 'webrtc_status',
            data: {
                timestamp: Date.now(),
                status: status,
                roomId: this.getRoomIdFromUrl()
            }
        });
    }

    /**
     * Report error to AI dev server
     */
    reportError(error, context = {}) {
        this.send({
            type: 'error_report',
            data: {
                timestamp: Date.now(),
                error: {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                },
                context: context,
                url: window.location.href
            }
        });
    }

    /**
     * Request AI assistance
     */
    requestAIAssistance(prompt, context = {}) {
        this.send({
            type: 'ai_request',
            data: {
                timestamp: Date.now(),
                prompt: prompt,
                context: context,
                url: window.location.href
            }
        });
    }

    /**
     * Get room ID from URL
     */
    getRoomIdFromUrl() {
        const path = window.location.pathname;
        return path.replace('/', '') || null;
    }

    /**
     * Check if we're in development
     */
    isDevelopment() {
        return window.location.hostname === 'localhost' || 
               window.location.hostname === '127.0.0.1' ||
               window.location.hostname.includes('dev') ||
               window.location.hostname.includes('localhost');
    }

    /**
     * Attempt to reconnect to AI dev server
     */
    attemptReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = this.retryDelay * this.reconnectAttempts;
            
            Utils.debug(`Attempting to reconnect to AI dev server (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
            
            setTimeout(() => {
                this.connect();
            }, delay);
        } else {
            Utils.debug('Max reconnection attempts reached for AI dev server');
            this.isAvailable = false;
        }
    }

    /**
     * Clean up resources
     */
    cleanup() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.isAvailable = false;
    }
}
